<template>
  <b-sidebar
    id="sidebar-yield-controlling"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    width="max(50%,700px)"
    @shown="onShow"
    @hidden="onHide"
  >
    <template #header="{ hide }">
      <div class="d-flex justify-content-between align-items-center w-100 ml-2">
        <div class="" v-if="isLoading"><b-skeleton width="30px" height="24px"></b-skeleton></div>
        <div v-else>
          <i class="fa-chevron-left fas icon-xl mr-2 navigationIcons" @click="goUpOneRow"></i>
          <i class="fa-chevron-right fas icon-xl mr-2 navigationIcons" @click="goDownOneRow"></i>
        </div>
        <div v-if="isLoading"><b-skeleton width="164px" height="24px"></b-skeleton></div>
        <div
          v-else-if="activeReisetermin"
          style="display: flex; justify-content: center; flex-direction: column"
        >
          <a :href="'#/reisetermine/' + activeReisetermin.id" target="_blank">
            <h4 class="mb-0">
              <b>{{ reiseterminkuerzelFormatted }}</b>
            </h4>
          </a>

          <span style="font-size: 1rem" v-if="activeReisetermin?.ursprungsreiseterminId">
            Ehemals:
            <a target="blank" :href="'/#/reisetermine/' + activeReisetermin.ursprungsreiseterminId">
              {{ activeReisetermin.ursprungsreisetermin?.reiseterminkuerzel }}
            </a>
          </span>
          <span style="font-size: 1rem" v-if="activeReisetermin?.ersatztermin">
            Ersatztermin:
            <a target="blank" :href="'/#/reisetermine/' + activeReisetermin.ersatztermin.id">
              {{ activeReisetermin.ersatztermin?.reiseterminkuerzel }}
            </a>
          </span>
        </div>

        <div v-if="isLoading"><b-skeleton></b-skeleton></div>
        <div v-else-if="activeReisetermin">
          <b-button
            class="btn btn-icon ml-auto mr-2 icon-button"
            size="sm"
            title="Reisestammdaten"
            :href="
              '#/reisen/' +
              activeReisetermin.reiseterminkuerzel?.substring(0, 6) +
              '/reisestammdaten?reiseterminAbreisedatum=' +
              activeReisetermin.abreisedatum.substring(0, 10)
            "
            target="_blank"
          >
            <i class="flaticon-edit" />
          </b-button>

          <b-button
            class="btn btn-icon ml-auto mr-2 icon-button"
            size="sm"
            title="Kontaktdatenliste"
            :href="kontaktdatenlisteLink"
            target="_blank"
          >
            <i class="flaticon2-user-outline-symbol" />
          </b-button>

          <div
            class="icon-button d-inline-flex justify-content-center align-middle mr-2 ml-auto"
            style="width: 32px; height: 32px"
            @click="openModalWithData"
            v-b-modal.modalAufgabeEdit
            v-b-tooltip.hover.bottom
            :title="'Aufgabe erstellen'"
          >
            <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
          </div>

          <b-button
            class="dialog-btn mr-4"
            size="sm"
            @click="saveChanges"
            :variant="disableSpeichernButton ? 'secondary' : 'primary'"
            :disabled="disableSpeichernButton"
          >
            Speichern
          </b-button>
          <i class="fas fa-times m-3 navigationIcons" @click="hide"></i>
        </div>
      </div>
    </template>
    <b-overlay :show="isLoading" opacity="0.95" style="min-height: 250px">
      <div
        class="px-6 py-4 d-flex"
        v-if="reiseterminExists && Object.keys(this.activeReisetermin).length > 0"
        style="width: 100%; flex-direction: column; gap: 30px"
      >
        <CollapsibleWithItemsEditable
          class="mt-4"
          header="Reisetermindaten"
          v-model="reisetermindaten"
          :isCollapsedOnMount="true"
        />
        <CollapsibleWithItemsEditable
          class="mt-4"
          header="Flugdaten"
          v-model="flugdaten"
          :isCollapsedOnMount="true"
        />

        <!-- Inlandsflüge -->
        <div v-if="activeReisetermin.metadata.inlandsfluege?.length > 0" class="mt-4">
          <h5
            role="button"
            @click="isInlandsfluegeCollapsed = !isInlandsfluegeCollapsed"
            :aria-expanded="isInlandsfluegeCollapsed ? 'true' : 'false'"
            aria-controls="collapse-4"
            class="mb-2 user-select-none"
          >
            <b-icon
              v-if="!isInlandsfluegeCollapsed"
              :scale="0.75"
              icon="chevron-right"
              aria-hidden="true"
            ></b-icon>
            <b-icon v-else :scale="0.75" icon="chevron-down" aria-hidden="true"></b-icon>
            Inlandsflüge
          </h5>
          <hr class="w-75 ml-0" />
          <b-collapse
            id="collapse-4"
            :class="isInlandsfluegeCollapsed ? null : 'collapsed'"
            v-model="isInlandsfluegeCollapsed"
          >
            <div v-for="(data, i) in dataInlandsfluege" :key="'inlandflug-' + i" class="mt-5">
              <h6 class="mb-4 ml-2">{{ i + 1 + '. Inlandsflug' }}</h6>
              <b-list-group
                horizontal
                v-for="item in data"
                :key="'inlandsflug-' + i + item.label"
                class="bv-example-row"
                :style="{ display: !item.show ? 'none' : '' }"
              >
                <b-list-group-item class="col-6 d-flex align-items-center detailLabelGreyedOut" style="">
                  <h6 class="my-1 leftLabel">{{ item.label }}</h6>
                </b-list-group-item>
                <b-list-group-item
                  class="col-6 d-flex align-items-center"
                  :class="'detailLabelGreyedOut'"
                  :variant="item.variant"
                >
                  <span>
                    <span v-html="item.renderer"></span>
                  </span>
                  <h6 class="my-1 leftLabel">{{ item.value }}</h6>
                </b-list-group-item>
              </b-list-group>
            </div>
          </b-collapse>
        </div>

        <!-- Einkaufsdaten -->
        <CollapsibleWithItemsEditable
          class="mt-4"
          header="Einkaufsdaten"
          v-model="einkaufsdaten"
          :isCollapsedOnMount="true"
        />

        <div v-if="aufgabenYieldControlling?.length > 0">
          <h5
            role="button"
            @click="isAufgabenCollapsed = !isAufgabenCollapsed"
            :aria-expanded="isAufgabenCollapsed ? 'false' : 'true'"
            aria-controls="collapse-5"
            class="mb-2 user-select-none mt-4"
          >
            <b-icon
              v-if="!isAufgabenCollapsed"
              :scale="0.75"
              icon="chevron-right"
              aria-hidden="true"
            ></b-icon>
            <b-icon v-else :scale="0.75" icon="chevron-down" aria-hidden="true"></b-icon>
            Aufgaben
          </h5>
          <hr class="w-75 ml-0" />
          <b-collapse
            id="collapse-5"
            :class="isAufgabenCollapsed ? null : 'collapsed'"
            v-model="isAufgabenCollapsed"
          >
            <AufgabenWidget
              class="mt-3"
              :filter="'sidebar-yield-controlling'"
              :ansicht="'sidebar-yield-controlling'"
              :aufgaben="aufgabenYieldControlling || []"
            ></AufgabenWidget>
          </b-collapse>
        </div>
      </div>
    </b-overlay>
  </b-sidebar>
</template>

<script>
import { format, add, getISODay } from 'date-fns';

import PaxWidget from '@/components/produkte/reisetermine/pax-widget.vue';
import CollapsibleWithItemsEditable from '@/components/common/collapsible-with-items-editable.vue';
import CollapsibleWithItems from '@/components/common/collapsible-with-items.vue';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import buildQuery from 'odata-query';
import apiService from '@/core/common/services/api.service';
import { translateEinkaufsartOptions, translateStatusGruppeOptions } from '@/core/common/helpers/utils.js';
import { ListChecks } from 'lucide-vue';

export default {
  components: { PaxWidget, CollapsibleWithItemsEditable, CollapsibleWithItems, AufgabenWidget, ListChecks },
  props: { reiseterminId: { type: Number } },
  data() {
    return {
      prefixedVerlinkungen: [],
      activeReisetermin: null,
      isLoading: false,
      reisetermindaten: [],
      flugdaten: [],
      einkaufsdaten: [],
      isInlandsfluegeCollapsed: true,
      isAufgabenCollapsed: false,
      resultsAirlines: [],
    };
  },
  watch: {
    reiseterminId: {
      immediate: true,
      async handler(id) {
        if (id != null) {
          await this.loadReisetermin(id);
          this.setReisetermindaten();
          this.setFlugdaten();
          this.setEinkaufsdaten();
        }
      },
    },
  },
  computed: {
    reiseterminExists() {
      return this.activeReisetermin && Object.keys(this.activeReisetermin).length > 0;
    },
    disableSpeichernButton() {
      return this.noFlugdatenChanges;
    },
    noFlugdatenChanges() {
      return this.flugdaten
        .filter(item => (!Array.isArray(item.value) && item.editable) || item.editable?.[0])
        .every(
          item =>
            item.value === item.valueEdited ||
            JSON.stringify(item.value)?.replaceAll('[', '').replaceAll(']', '') ===
              JSON.stringify(item.valueEdited)?.replaceAll('[', '').replaceAll(']', '')
        );
    },
    kontaktdatenlisteLink() {
      if (!this.activeReisetermin) {
        return null;
      }
      const startdatumYear = new Date(this.activeReisetermin.startdatum).getFullYear();
      return startdatumYear === 2022
        ? 'https://docs.google.com/spreadsheets/d/1jLZQswDFvOvXLT9qlg44QPP5tDp0mcvt5z0PYof_GG8/edit?usp=sharing'
        : startdatumYear === 2023
        ? 'https://docs.google.com/spreadsheets/d/1ISyLNU-nPPi_M7Yrtm_r55Q789Y_9xdpUlRXZpbW-5k/edit#gid=0'
        : startdatumYear === 2024
        ? 'https://docs.google.com/spreadsheets/d/1mf6SBNPv8NNBUulHYNA1XGyKcPxrp3nsRz7Nyx6Har0/edit#gid=0'
        : startdatumYear === 2025 || startdatumYear === 2026
        ? 'https://docs.google.com/spreadsheets/d/1OkrSdCu1SskISUsioOWzJhi1M4lCqhAtScsvcdFMrWo/edit?gid=0#gid=0'
        : null;
    },
    aufgabenYieldControlling() {
      if (!this.activeReisetermin || this.activeReisetermin.aufgaben?.length === 0) {
        return null;
      }

      return this.activeReisetermin.aufgaben;
    },
    dataInlandsfluege() {
      if (this.reiseterminExists) {
        return this.activeReisetermin.metadata.inlandsfluege
          .slice()
          .sort((a, b) => (a.tag === b.tag ? 0 : a.tag > b.tag ? 1 : -1))
          .map(inlandsflug => {
            return [
              {
                label: 'Tag / Verkehrstag / Datum',
                value:
                  inlandsflug.tag +
                  ' / ' +
                  this.getVerkehrstagOfInlandsflug(this.activeReisetermin, inlandsflug) +
                  ' / ' +
                  this.getDateOfIlandsflug(this.activeReisetermin, inlandsflug),
                renderer: null,
                variant: null,
                show: true,
              },
              {
                label: 'Abflughafen',
                value: inlandsflug.abflughafenIataCode,
                renderer: null,
                variant: null,
                show: true,
              },
              {
                label: 'Zielflughafen',
                value: inlandsflug.zielflughafenIataCode,
                renderer: null,
                variant: null,
                show: true,
              },
              {
                label: 'Früh. Abflug',
                value: inlandsflug.fruehesterAbflug?.substring(0, 5) ?? '-',
                renderer: null,
                variant: null,
                show: inlandsflug.minimaleUmsteigezeit == null,
              },
              {
                label: 'Spät. Ankunft',
                value: inlandsflug.spaetesteAnkunft?.substring(0, 5) ?? '-',
                renderer: null,
                variant: null,
                show: inlandsflug.minimaleUmsteigezeit == null,
              },
              {
                label: 'Id',
                value: inlandsflug.id,
                show: false,
              },
            ];
          });
      } else return [];
    },
    reiseterminkuerzelFormatted() {
      if (this.activeReisetermin) {
        const rtKuerzel = this.activeReisetermin.reiseterminkuerzel;
        const startdatum = this.activeReisetermin.startdatum;
        return rtKuerzel.substring(0, 6) + '  ' + format(new Date(startdatum), 'dd.MM.yyyy');
      } else return '';
    },
  },
  methods: {
    async loadReisetermin(id) {
      const odataQuery = buildQuery({
        filter: [{ id: { eq: id } }],
        expand: {
          reise: {},
          flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
          gueltigeSaison: {
            expand: { ziellandZielflughaefen: {}, ziellandAbflughaefen: {}, Inlandsfluege: {} },
          },
          gueltigerSaisonpreis: { select: ['thMaxLangstrecke', 'thMaxInlandsflug'] },
          metadata: {
            expand: {
              ziellandZielflughaefen: {},
              ziellandAbflughaefen: {},
              Inlandsfluege: {},
            },
            select: [
              'minPax',
              'maxPax',
              'kalkPax',
              'forecastPax',
              'durchfuehrbareMinPax',
              'planPax',
              'hotelnaechte',
              'transferszenarioHin',
              'transferszenarioRueck',
              'fruehesteAnkunft',
              'spaetesteAnkunft',
              'fruehesterAbflug',
              'spaetesterAbflug',
              'restriktionenHinRollover',
              'restriktionenRueckRollover',
              'overnightOutbound',
              'overnightInbound',
            ],
          },
          aufgaben: {},
          ursprungsreisetermin: {},
          ersatztermin: {},
          pax: {},
        },
      });
      this.isLoading = true;
      const response = await apiService.get('/Reisetermin', odataQuery);
      this.activeReisetermin = response.data.result.value[0];
      this.isLoading = false;
    },
    setReisetermindaten() {
      this.reisetermindaten = [
        {
          label: 'Priorität',
          value: this.activeReisetermin.reise.prioritaet,
          renderer: 'prioritaetWidget',
          variant: null,
        },
        {
          label: 'Pax',
          value: this.activeReisetermin,
          renderer: 'paxWidget',
          variant: null,
          className: 'mb-2',
        },
        {
          label: 'Plan Pax',
          value: this.activeReisetermin.metadata?.planPax ?? '-',
          variant: null,
          renderer: null,
        },
        {
          label: 'Hotelnächte',
          value: this.activeReisetermin.metadata?.hotelnaechte ?? '-',
          variant: null,
          renderer: null,
        },
        {
          label: 'Transferszenario Hin/Rück',
          value: null,
          renderer: `<h6 class="my-1" style="font-size: 14.3px;">
                ${
                  (this.activeReisetermin.metadata.transferszenarioHin?.substring(0, 2) ?? '-') +
                  ' | ' +
                  (this.activeReisetermin.metadata.transferszenarioRueck?.substring(0, 2) ?? '-')
                }
                </h6>`,
          variant: null,
        },
      ];
    },
    setFlugdaten() {
      this.flugdaten = [
        {
          label: 'Strecke',
          value:
            (this.activeReisetermin.metadata.ziellandZielflughaefen
              ?.map(fh => fh.flughafenIataCode)
              .join('/') ?? '') +
            '-' +
            (this.activeReisetermin.metadata.ziellandAbflughaefen
              ?.map(fh => fh.flughafenIataCode)
              .join('/') ?? ''),
          renderer: null,
          variant: null,
        },
        {
          label: ['OB', 'IB'],
          value: [
            this.activeReisetermin?.metadata.overnightOutbound ?? '-',
            this.activeReisetermin?.metadata.overnightInbound ?? '-',
          ],
          renderer: null,
          editable: [false, false],
          split: true,
          paramLabel: ['overnightOutbound', 'overnightInbound'],
        },
        {
          label: 'Abreise',
          value: this.activeReisetermin.abreisedatum
            ? format(new Date(this.activeReisetermin.abreisedatum), 'dd.MM.yyyy')
            : '-',
          renderer: null,
          variant: null,
        },
        {
          label: 'Rückflugdatum',
          value: this.activeReisetermin.hotelendUndRueckflugdatum
            ? format(new Date(this.activeReisetermin.hotelendUndRueckflugdatum), 'dd.MM.yyyy')
            : '-',
          renderer: null,
          variant: null,
        },
        {
          label: ['Verkehrstag Hin', 'Rück'],
          value: [this.activeReisetermin?.verkehrstagHin, this.activeReisetermin?.verkehrstagRueck],
          renderer: null,
          editable: [false, false],
          split: true,
          paramLabel: ['verkehrstagHin', 'verkehrstagRück'],
        },
        {
          label: ['Hin: Früheste', 'Späteste Ankunft'],
          value: [
            `${this.activeReisetermin?.metadata?.fruehesteAnkunft?.substring(0, 5)} Uhr`,
            `${this.activeReisetermin?.metadata?.spaetesteAnkunft?.substring(0, 5)} Uhr ${
              this.activeReisetermin?.metadata.restriktionenHinRollover ? '+1' : ''
            }`,
          ],

          renderer: null,
          editable: [false, false],
          editType: ['time', 'time'],
          split: true,
          paramLabel: ['fruehesteAnkunft', 'spaetesteAnkunft'],
        },
        {
          label: ['Rück: Frühester', 'Spätester Abflug'],
          value: [
            `${this.activeReisetermin?.metadata?.fruehesterAbflug?.substring(0, 5)} Uhr`,
            `${this.activeReisetermin?.metadata?.spaetesterAbflug?.substring(0, 5)} Uhr ${
              this.activeReisetermin?.metadata.restriktionenRueckRollover ? '+1' : ''
            }`,
          ],
          renderer: null,
          editable: [false, false],
          editType: ['time', 'time'],
          split: true,
          paramLabel: ['fruehesterAbflug', 'spaetesterAbflug'],
        },
        {
          label: ['TH Langstrecke', 'TH Inlandsflug'],
          value: [this.activeReisetermin?.thLangstrecke ?? '-', this.activeReisetermin?.thInlandsflug ?? '-'],
          valueEdited: [
            this.activeReisetermin?.thLangstrecke ?? '-',
            this.activeReisetermin?.thInlandsflug ?? '-',
          ],
          renderer: null,
          editable: [true, true],
          editType: ['number', 'number'],
          split: true,
          paramLabel: ['thLangstrecke', 'thInlandsflug'],
          append: ['€', '€'],
        },
        {
          label: ['TH Max Langstrecke', 'TH Max Inlandsflug'],
          value: [
            this.activeReisetermin?.gueltigerSaisonpreis?.thMaxLangstrecke ?? '-',
            this.activeReisetermin?.gueltigerSaisonpreis?.thMaxInlandsflug ?? '-',
          ],
          renderer: null,
          editable: [false, false],
          editType: ['number', 'number'],
          split: true,
          append: ['€', '€'],
        },
      ];
    },
    setEinkaufsdaten() {
      this.einkaufsdaten = [
        {
          label: 'Status Gruppe',
          value: this.activeReisetermin.flugeinkauf?.statusgruppe ?? '-',
          renderer: `<h6 style="font-size: 14.3px;">
                ${translateStatusGruppeOptions(this.activeReisetermin.flugeinkauf?.statusgruppe)}</h6>`,
          editable: false,
        },
        {
          label: 'Einkaufsart',
          value: this.activeReisetermin.flugeinkauf?.einkaufsart,
          renderer: `<h6 style="font-size: 14.3px;">
                ${translateEinkaufsartOptions(this.activeReisetermin.flugeinkauf?.einkaufsart)}</h6>`,
          editable: false,
        },
        {
          label: 'GRP FH (Gesamtpreis GRP)',
          value:
            this.activeReisetermin.flugeinkauf?.gruppeneinkaeufe?.length > 0
              ? this.activeReisetermin.flugeinkauf.gruppeneinkaeufe
                  .map(
                    grp =>
                      `${grp.flughafenIataCode || ''}(${
                        grp.gesamtpreisGruppeneinkauf ? Math.round(grp.gesamtpreisGruppeneinkauf) + '€' : '0€'
                      })`
                  )
                  .filter(Boolean)
                  .join(', ')
              : '-',
          renderer: null,
          editable: false,
        },
      ];
    },

    goUpOneRow() {
      this.$emit('rowSelectionChanged', 'up');
    },
    goDownOneRow() {
      this.$emit('rowSelectionChanged', 'down');
    },
    async saveChanges() {
      this.isLoading = true;

      const thWerteParams = this.formatTHWerte();
      const reisekuerzel = this.activeReisetermin.reiseterminkuerzel.substring(0, 6);

      // Prüfe TH-Werte Änderungen
      const objectThWerte = this.flugdaten.find(element => element.label[0] === 'TH Langstrecke');
      const isChangedThWerte =
        objectThWerte.value[0] != objectThWerte.valueEdited[0] ||
        objectThWerte.value[1] != objectThWerte.valueEdited[1];

      try {
        // API-Aufruf für TH-Werte wenn verändert wurden
        if (isChangedThWerte) {
          await apiService
            .put(`Reise/${reisekuerzel}/reisetermine/bulkUpdateReiseterminValues`, thWerteParams)
            .then(response => {
              this.$emit('thWerteBearbeitet', response);
              this.toast('TH-Werte erfolgreich gespeichert.');
            });
        }

        await this.loadReisetermin(this.reiseterminId);
        this.setReisetermindaten();
        this.setFlugdaten();
        this.setEinkaufsdaten();
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.toast('Fehler beim Speichern der Daten.', 'error');
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },
    formatTHWerte() {
      // Hole das TH-Werte Objekt aus flugdaten
      const objectThWerte = this.flugdaten.find(element => element.label[0] === 'TH Langstrecke');

      const thWerteParams = {
        [this.activeReisetermin.startdatum]: {
          thLangstrecke: objectThWerte.valueEdited[0] === '-' ? null : +objectThWerte.valueEdited[0] || null,
          thInlandsflug: objectThWerte.valueEdited[1] === '-' ? null : +objectThWerte.valueEdited[1] || null,
        },
      };

      console.log('formatTHWerte result:', thWerteParams);
      return thWerteParams;
    },
    openModalWithData() {
      this.prefixedVerlinkungen = [
        {
          id: 0,
          typ: 'Reisetermin',
          artifactId: this.activeReisetermin.id,
          artifactText: this.activeReisetermin.reiseterminkuerzel,
        },
      ];
      this.$emit('showModalAufgabeEdit', this.prefixedVerlinkungen);
    },
    onShow() {
      this.addKeyEventListener();
    },
    onHide() {
      this.resultsAirlines = [];
      this.$emit('sidebarHide');
      this.removeKeyEventListener();
    },
    addKeyEventListener() {
      // Placeholder implementation
    },
    removeKeyEventListener() {
      // Placeholder implementation
    },
    getVerkehrstagOfInlandsflug(reisetermin, inlandsflug) {
      const dayOfInlandsflug = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      const verkehrstag = getISODay(dayOfInlandsflug);
      return verkehrstag;
    },
    getDateOfIlandsflug(reisetermin, inlandsflug) {
      const dateOfInlandsfluge = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      return format(dateOfInlandsfluge, 'dd.MM.yyyy');
    },
  },
};
</script>

<style scoped>
.btn.btn-icon.icon-button {
  width: 32px;
  height: 32px;
}

.navigationIcons {
  color: black;
}

.navigationIcons:hover {
  color: #0d6efd;
  cursor: pointer;
}

.normalFormatted {
  border-style: solid;
  border-color: rgba(114, 114, 114, 0.15);
}

.detailLabelGreyedOut {
  background-color: rgba(114, 114, 114, 0.05);
  border-style: solid;
  border-color: rgba(114, 114, 114, 0.15);
}

.leftLabel {
  font-size: 1.1rem;
  font-weight: 500;
}

.list-group {
  height: 42px;
}

:deep(.form-control) {
  height: 32px !important;
}

.plus-sign {
  height: 24px;
  width: 24px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: white;
  cursor: pointer;
  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* IE 10 and IE 11 */
  user-select: none;
  /* Standard syntax */
}
</style>
